// Shared therapist resolver for visit/projection reports (preview + emailed PDF).
// Order: linked profile id (personnel id / therapist_profile_id) -> full name -> name_aliases.

export const normalizeName = (name) => {
  let n = String(name || '').trim();
  if (n.includes(',')) {
    const idx = n.indexOf(',');
    n = `${n.slice(idx + 1)} ${n.slice(0, idx)}`;
  }
  // Mirrors backend utils/name_matching.normalize_name so review keys match.
  const cleaned = n.normalize('NFKD').replace(/[^\x00-\x7F]/g, '').toLowerCase()
    .replace(/[^a-z\s\-']/g, ' ').replace(/'/g, '');
  return cleaned.replace(/-/g, ' - ').split(/\s+/).filter(Boolean).join(' ').replace(/ - /g, '-');
};

export const personnelDisplayName = (p) => `${p.first_name || ''} ${p.last_name || ''}`.replace(/\s+/g, ' ').trim();

export const buildPersonnelResolver = (personnel = []) => {
  const byId = {};
  const byName = {};
  const byAlias = {};
  personnel.forEach((p) => {
    if (!p) return;
    if (p.id) byId[p.id] = p;
    if (p.therapist_profile_id && !byId[p.therapist_profile_id]) byId[p.therapist_profile_id] = p;
    const full = normalizeName(personnelDisplayName(p));
    if (full && !byName[full]) byName[full] = p;
    (p.name_aliases || []).forEach((a) => {
      const k = normalizeName(a);
      if (k && !byAlias[k]) byAlias[k] = p;
    });
  });
  // Returns the matching personnel record or null.
  return ({ profileId, name } = {}) => {
    if (profileId && byId[profileId]) return byId[profileId];
    const k = normalizeName(name);
    if (!k) return null;
    return byName[k] || byAlias[k] || null;
  };
};

// One report row per personnel record. Unmatched names awaiting an admin decision
// show as 'Pending review'; otherwise the uploaded name + 'Other'.
export const resolveTherapistRow = (resolve, { profileId, name }, pendingKeys) => {
  const p = resolve({ profileId, name });
  if (p) {
    return {
      key: `p:${p.id}`,
      name: personnelDisplayName(p),
      discipline: p.discipline || '',
      ideal_visits: p.ideal_visits ?? null,
      state: p.state || 'Other',
      last_name: p.last_name || '',
      personnel_id: p.id,
    };
  }
  const raw = String(name || '').replace(/\s+/g, ' ').trim();
  const pending = !!pendingKeys && pendingKeys.has(normalizeName(raw));
  return {
    pending_review: pending,
    key: `n:${normalizeName(raw)}`,
    name: raw,
    discipline: '',
    ideal_visits: null,
    state: pending ? 'Pending review' : 'Other',
    last_name: raw.split(' ').slice(-1)[0] || '',
    personnel_id: null,
  };
};

// Name keys of uploads awaiting review (admin-only endpoint; empty set on failure).
export const fetchPendingNameKeys = async (axios, API) => {
  try {
    const { data } = await axios.get(`${API}/name-review/summary`);
    return new Set(data?.pending_keys || []);
  } catch (e) {
    return new Set();
  }
};
